import React from 'react';
import { Layout as LayoutIcon, Trash2 } from 'lucide-react';
import { useTranslation } from "react-i18next";

interface ChartHeaderProps {
    title: string;
    description?: string;
    summary?: React.ReactNode;
    actions?: React.ReactNode;
    onRemove?: () => void;
    isEditMode?: boolean;
}

/**
 * Shared Header for all charts
 * Matches the Template Studio design from admin
 */
export const ChartHeader: React.FC<ChartHeaderProps> = ({
    title,
    summary,
    actions,
    onRemove,
    isEditMode = false
}) => {
    const { t } = useTranslation("common");
    return (
        <div className={`chart-drag-handle flex items-center justify-between px-3 py-2 border-b border-neutral-50 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 ${isEditMode ? 'cursor-move' : ''}`}>
            <div className="flex items-center gap-2 overflow-hidden flex-1">
                <div className="w-4 h-4 rounded bg-transparent dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 flex items-center justify-center shrink-0">
                    <LayoutIcon size={11} className="text-neutral-500 dark:text-neutral-400" />
                </div>
                <h3 className="text-[11px] font-bold text-neutral-700 dark:text-neutral-300 truncate capitalize">
                    {title}
                </h3>
            </div>

            <div className="flex items-center gap-2 shrink-0">
                {/* Performance / Summary Values */}
                {summary && (
                    <div className="flex items-center gap-2 mr-1 text-[10px] font-medium">
                        {summary}
                    </div>
                )}

                {/* Additional Actions */}
                {actions}

                {/* Delete Button - matches Template Studio exactly */}
                {isEditMode && onRemove && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onRemove();
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                        }}
                        className="p-1 text-neutral-400 dark:text-neutral-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded transition-colors relative z-10"
                        title={t("charts.components.removeChart") || "Remove Chart"}
                    >
                        <Trash2 size={12} />
                    </button>
                )}
            </div>
        </div>
    );
};
